/**
 * Font API Service
 * 
 * Handles all API calls related to font management
 */

import axios from 'axios';

const API_BASE = '/api/v1';

export interface EnabledFont {
  id: string;
  realmId: string;
  fontUid: string;
  fontFamily: string;
  displayName: string;
  weight: number;
  style: string;
  category: string;
  courtCompliant: boolean;
  enabled: boolean;
  isCustom: boolean;
  fontFileUrl?: string;
}

/**
 * Get authentication token from Keycloak or storage
 */
function getAuthToken(): string | null {
  // Try Keycloak instance first (primary method)
  const keycloak = (window as any).keycloak;
  if (keycloak?.token) {
    return keycloak.token;
  }

  // Fallback to localStorage/sessionStorage
  return (
    localStorage.getItem('webelieve_token') ||
    sessionStorage.getItem('webelieve_token') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token') ||
    localStorage.getItem('webelieve_access_token')
  );
}

/**
 * Get enabled fonts for the current user's realm
 * This is used by EdgeDocs toolbar to populate the font dropdown
 */
export async function getEnabledFonts(): Promise<EnabledFont[]> {
  try {
    const token = getAuthToken();
    
    if (!token) {
      console.warn('[FontAPI] No auth token available');
      return [];
    }

    const response = await axios.get(`${API_BASE}/fonts/enabled`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (response.data?.success && Array.isArray(response.data.data)) {
      console.log(`[FontAPI] Loaded ${response.data.data.length} enabled fonts from API`);
      return response.data.data;
    }

    console.warn('[FontAPI] Unexpected response format:', response.data);
    return [];

  } catch (error: any) {
    console.error('[FontAPI] Failed to fetch enabled fonts:', error.message);
    
    // Don't throw - return empty array to prevent UI breakage
    // The toolbar will show a message if no fonts are available
    return [];
  }
}

/**
 * Font family with grouped variants
 */
export interface FontFamily {
  familyName: string;
  variants: EnabledFont[];
  category: string;
}

/**
 * Get enabled fonts grouped by family
 * Used for cascading dropdown (Google Docs style)
 */
export async function getEnabledFontFamilies(): Promise<FontFamily[]> {
  const fonts = await getEnabledFonts();
  
  // Group fonts by family
  const familyMap = new Map<string, FontFamily>();
  
  fonts.forEach(font => {
    const family = font.fontFamily || 'Unknown';
    
    if (!familyMap.has(family)) {
      familyMap.set(family, {
        familyName: family,
        variants: [],
        category: font.category
      });
    }
    
    familyMap.get(family)!.variants.push(font);
  });
  
  // Sort families alphabetically
  const families = Array.from(familyMap.values()).sort((a, b) => 
    a.familyName.localeCompare(b.familyName)
  );
  
  // Sort variants within each family (Regular first, then by weight, then by style)
  families.forEach(family => {
    family.variants.sort((a, b) => {
      // Regular first
      if (a.style === 'normal' && a.weight === 400) return -1;
      if (b.style === 'normal' && b.weight === 400) return 1;
      
      // Then by weight
      if (a.weight !== b.weight) return a.weight - b.weight;
      
      // Then by style (normal before italic)
      if (a.style !== b.style) return a.style === 'normal' ? -1 : 1;
      
      return 0;
    });
  });
  
  return families;
}

/**
 * Log font usage for analytics (fire-and-forget)
 */
export async function logFontUsage(
  fontFamily: string,
  documentId?: string,
  fontSize?: number
): Promise<void> {
  try {
    const token = getAuthToken();
    
    if (!token) {
      return; // Silently skip if no auth
    }

    // Fire-and-forget - don't await
    axios.post(
      `${API_BASE}/fonts/usage`,
      { fontFamily, documentId, fontSize },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(() => {
      // Silently fail - analytics shouldn't block user
    });

  } catch (error) {
    // Silently fail - analytics shouldn't block user
  }
}

// ============================================================================
// ADMIN FONT MANAGEMENT API
// ============================================================================

export interface RealmFont {
  id: string;
  realmId: string;
  fontUid: string;
  displayName: string;
  fontFamily: string;
  weight: number;
  style: string;
  category: string;
  courtCompliant: boolean;
  enabled: boolean;
  isCustom: boolean;
  fontFileUrl?: string;
  fontFileSize?: number;
  fontFormat?: string;
  uploadedBy?: string;
  uploadedAt: string;
  updatedAt: string;
  usageCount?: number;
  metadata?: any;
}

export interface FontDefault {
  id: string;
  realm_id: string;
  context: 'global' | 'template' | 'document-type';
  context_value: string | null;
  font_family: string;
  font_size: number;
  font_weight: number;
  created_at: string;
  updated_at: string;
}

export interface FontUsageStats {
  font_family: string;
  usage_count: number;
  last_used: string;
}

/**
 * Get all fonts for the current realm (admin)
 */
export async function getAllFonts(): Promise<RealmFont[]> {
  const token = getAuthToken();
  if (!token) throw new Error('No authentication token');

  const response = await axios.get(`${API_BASE}/admin/fonts`, {
    headers: { Authorization: `Bearer ${token}` }
  });

  return response.data.success ? response.data.data : [];
}

/**
 * Update font enabled/disabled status (admin)
 */
export async function updateFontStatus(fontUid: string, enabled: boolean): Promise<void> {
  const token = getAuthToken();
  if (!token) throw new Error('No authentication token');

  await axios.patch(
    `${API_BASE}/admin/fonts/${fontUid}`,
    { enabled },
    { headers: { Authorization: `Bearer ${token}` } }
  );
}

/**
 * Upload a custom font file (admin)
 */
export async function uploadCustomFont(formData: FormData): Promise<RealmFont> {
  const token = getAuthToken();
  if (!token) throw new Error('No authentication token');

  const response = await axios.post(
    `${API_BASE}/admin/fonts/upload`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    }
  );

  return response.data.data;
}

/**
 * Get font defaults for templates/document types (admin)
 */
export async function getFontDefaults(): Promise<FontDefault[]> {
  const token = getAuthToken();
  if (!token) throw new Error('No authentication token');

  const response = await axios.get(`${API_BASE}/admin/fonts/defaults`, {
    headers: { Authorization: `Bearer ${token}` }
  });

  return response.data.success ? response.data.data : [];
}

/**
 * Save font defaults (admin)
 */
export async function saveFontDefaults(defaults: Partial<FontDefault>[]): Promise<void> {
  const token = getAuthToken();
  if (!token) throw new Error('No authentication token');

  await axios.post(
    `${API_BASE}/admin/fonts/defaults`,
    { defaults },
    { headers: { Authorization: `Bearer ${token}` } }
  );
}

/**
 * Get font usage statistics (admin)
 */
export async function getFontUsageStats(): Promise<FontUsageStats[]> {
  const token = getAuthToken();
  if (!token) throw new Error('No authentication token');

  const response = await axios.get(`${API_BASE}/admin/fonts/usage`, {
    headers: { Authorization: `Bearer ${token}` }
  });

  return response.data.success ? response.data.data : [];
}

/**
 * Sync font manifest to database (admin)
 * This populates the database with all fonts from the manifest
 */
export async function syncFontManifest(): Promise<{ totalFonts: number; newFonts: number; existingFonts: number; message: string }> {
  const token = getAuthToken();
  if (!token) throw new Error('No authentication token');

  const response = await axios.post(
    `${API_BASE}/admin/fonts/sync`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );

  // Backend returns: { success, data: { totalFonts, newFonts, existingFonts }, message }
  return {
    ...response.data.data,
    message: response.data.message
  };
}

